// ---------------------------------------------------------------------------
// Alter im Text (PF 23, Roadmap AP-40): Der Text nennt das Alter einer Figur („der zwölfjährige Max“, „Max war zwölf Jahre alt“), die
// Lebensdaten sagen etwas anderes. Gelesen wird der sichtbare Text eines Absatzes mit seinen Verweisen – so arbeiten Prüfung (Markdown)
// und Editor (Absätze) mit derselben Erkennung. Nur Personen, nur mit Geburt und vergleichbarer Zeit; ein Jahr Spielraum, wenn eine der
// Zeiten ungefähr ist (ungefähr markiert, nur aufs Jahr bzw. den Monat genau).
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { ageAt } from './lifespan.js';
import { refIndex } from './refs.js';
import { stateEvents, stateRoleOf } from './states.js';
import type { Entity, IgnoredTerm, PriorSource, StoryNode } from './types.js';

/** Ein Verweis im sichtbaren Text: [von, bis) und sein Schlüssel */
export interface TextRef {
  from: number;
  to: number;
  key: string;
}

/** Eine Altersangabe im Text: die Stelle [von, bis), das genannte Alter und der Verweis auf die Figur */
export interface AgeClaim {
  from: number;
  to: number;
  years: number;
  key: string;
}

const UNITS: Record<string, number> = { ein: 1, eins: 1, eine: 1, zwei: 2, drei: 3, vier: 4, fünf: 5, sechs: 6, sieben: 7, acht: 8, neun: 9 };
const TEENS: Record<string, number> = { zehn: 10, elf: 11, zwölf: 12, dreizehn: 13, vierzehn: 14, fünfzehn: 15, sechzehn: 16, siebzehn: 17, achtzehn: 18, neunzehn: 19 };
const TENS: Record<string, number> = { zwanzig: 20, dreißig: 30, vierzig: 40, fünfzig: 50, sechzig: 60, siebzig: 70, achtzig: 80, neunzig: 90 };

/** Eine Zahl als Ziffern oder deutsches Zahlwort bis 199 („zwölf“, „dreiundzwanzig“, „hundertzwei“); null, wenn es keine ist */
export function germanNumber(word: string): number | null {
  const w = word.toLowerCase();
  if (/^\d{1,3}$/.test(w)) return Number(w);
  const below100 = (s: string): number | null => {
    if (s === '') return 0;
    if (s in UNITS && s !== 'eine') return UNITS[s]!;
    if (s in TEENS) return TEENS[s]!;
    if (s in TENS) return TENS[s]!;
    const m = /^(ein|zwei|drei|vier|fünf|sechs|sieben|acht|neun)und(zwanzig|dreißig|vierzig|fünfzig|sechzig|siebzig|achtzig|neunzig)$/.exec(s);
    return m ? UNITS[m[1]!]! + TENS[m[2]!]! : null;
  };
  const hundred = /^(?:ein)?hundert(?:und)?(.*)$/.exec(w);
  if (hundred) {
    const rest = below100(hundred[1]!);
    return rest === null ? null : 100 + rest;
  }
  const n = below100(w);
  return n === null || n === 0 ? null : n;
}

const NUM = '(\\d{1,3}|[\\p{L}]+?)';
/** „der zwölfjährige“, „die 12-jährige“, „zwölf Jahre alte“ – unmittelbar (höchstens zwei Wörter dazwischen) vor dem Verweis */
const BEFORE = new RegExp(`(?<![\\p{L}\\p{N}])(?:${NUM}[-‐]?jährige[nmrs]?|${NUM}\\s+Jahre\\s+alte[nmrs]?)(?:\\s+[\\p{Ll}][\\p{L}-]*){0,2}\\s+$`, 'iu');
/** „ist zwölf Jahre alt“, „, zwölf Jahre alt“, „war gerade erst zwölf“ (nur mit „Jahre alt“) – nach dem Verweis im selben Satz */
const AFTER = new RegExp(`^(?:,\\s*|\\s+(?:ist|war|wird|wurde|sei|wäre)\\s+(?:(?:gerade|erst|schon|bereits|eben|knapp|fast|kaum|noch)\\s+){0,2})${NUM}\\s+Jahre?\\s+alt(?![\\p{L}])`, 'iu');

/** Die Altersangaben eines Absatzes (sichtbarer Text mit seinen Verweisen) */
export function ageClaims(text: string, refs: readonly TextRef[]): AgeClaim[] {
  const out: AgeClaim[] = [];
  const sorted = [...refs].sort((a, b) => a.from - b.from);
  sorted.forEach((r, i) => {
    // davor: bis zum vorigen Verweis bzw. Satzende
    const prev = sorted[i - 1]?.to ?? 0;
    const head = text.slice(Math.max(prev, r.from - 80), r.from);
    const cut = Math.max(head.lastIndexOf('.'), head.lastIndexOf('!'), head.lastIndexOf('?'), head.lastIndexOf('\n'));
    const before = head.slice(cut + 1);
    const b = BEFORE.exec(before);
    if (b) {
      const years = germanNumber(b[1] ?? b[2] ?? '');
      if (years !== null) {
        // die Stelle beginnt beim Artikel davor, sofern einer da ist
        const start = r.from - before.length + b.index;
        const article = /(?:^|\s)((?:der|die|das|den|dem|des|ein|eine|einen|einem|eines|einer)\s+)$/iu.exec(text.slice(Math.max(0, start - 12), start));
        out.push({ from: article ? start - article[1]!.length : start, to: r.to, years, key: r.key });
        return;
      }
    }
    // danach: bis zum nächsten Verweis bzw. Satzende
    const next = sorted[i + 1]?.from ?? text.length;
    const tail = text.slice(r.to, Math.min(next, r.to + 80));
    const a = AFTER.exec(tail);
    if (a) {
      const years = germanNumber(a[1]!);
      if (years !== null) out.push({ from: r.from, to: r.to + a[0].length, years, key: r.key });
    }
  });
  return out;
}

/** Ein Alter im Text, das nicht zu den Lebensdaten passt: genannt `years`, laut Geburt `actual` (in der Szene `bornSceneId`) */
export interface AgeConflict extends AgeClaim {
  entityId: string;
  actual: number;
  bornSceneId: string;
  /** geboren in einer früheren Episode der Serie (`bornSceneId` leer) */
  bornPrior?: PriorSource;
}

const keyOf = (ctx: StoryCtx, id: string): string => ctx.entities.get(id)?.refKey ?? id;

/** „Ist Absicht“ (sie macht sich jünger, er lügt …): für diese Figur mit diesem Alter, gemerkt im Dokument */
export const ageIgnore = (ctx: StoryCtx, c: Pick<AgeConflict, 'entityId' | 'years'>): IgnoredTerm => ({ term: '~alter', before: `@${keyOf(ctx, c.entityId)}`, after: String(c.years) });

/** Änderungen der Verfassung in Weltzeit, einmal je Stand der Geschichte (die Prüfung fragt je Absatz) */
const eventsCache = new WeakMap<StoryCtx, ReturnType<typeof stateEvents>>();
const eventsOf = (ctx: StoryCtx): ReturnType<typeof stateEvents> => {
  let all = eventsCache.get(ctx);
  if (!all) eventsCache.set(ctx, (all = stateEvents(ctx)));
  return all;
};

const rough = (n: StoryNode): boolean => !!n.time?.approx || n.time?.precision === 'year' || n.time?.precision === 'month';

/**
 * Altersangaben in einem Absatz der Szene, die nicht zu den Lebensdaten passen (ohne „ist Absicht“). Nur lebende Personen mit Geburt
 * und vergleichbarer Zeit; tot bzw. ungeboren meldet schon „Lebt die Figur hier?“.
 */
export function ageTextConflicts(ctx: StoryCtx, scene: StoryNode, text: string, refs: readonly TextRef[], ignored?: readonly IgnoredTerm[], index: ReadonlyMap<string, Entity> = refIndex(ctx)): AgeConflict[] {
  if (refs.length === 0 || !/jährig|Jahre?\s+alt/iu.test(text)) return [];
  const all = eventsOf(ctx);
  return ageClaims(text, refs).flatMap((claim) => {
    const e = index.get(claim.key);
    if (!e || stateRoleOf(ctx, e.id) !== 'person') return [];
    const at = ageAt(ctx, e.id, scene.id, all);
    if (!at || at.state !== 'alive' || !at.age) return [];
    const slack = at.age.approx || rough(scene) ? 1 : 0;
    if (Math.abs(at.age.years - claim.years) <= slack) return [];
    const c: AgeConflict = { ...claim, entityId: e.id, actual: at.age.years, bornSceneId: at.born.sceneId, ...(at.born.prior ? { bornPrior: at.born.prior } : {}) };
    const entry = ageIgnore(ctx, c);
    return ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after) ? [] : [c];
  });
}
